// Central signal for a server-rejected bearer session.
//
// API clients report only 401 responses that were sent with a Bearer token.
// AuthProvider owns the user-facing response (clear stale state, preserve the
// route, and navigate to login). Keeping navigation out of Axios avoids a
// router dependency and lets us reject stale responses from an older token.

export interface RejectedSession {
  token: string;
  requestPath?: string;
}

type RejectedSessionListener = (rejection: RejectedSession) => void;

const listeners = new Set<RejectedSessionListener>();
let lastReport: { token: string; reportedAt: number } | null = null;
let reportSuppressionDepth = 0;
let pendingSuppressedReport: RejectedSession | null = null;
const REPORT_DEDUPE_MS = 1_000;

export const reportRejectedSession = (rejection: RejectedSession): void => {
  if (reportSuppressionDepth > 0) {
    pendingSuppressedReport = rejection;
    return;
  }

  const now = Date.now();
  if (
    lastReport
    && lastReport.token === rejection.token
    && now - lastReport.reportedAt < REPORT_DEDUPE_MS
  ) return;

  lastReport = { token: rejection.token, reportedAt: now };
  listeners.forEach((listener) => listener(rejection));
};

export const subscribeToRejectedSession = (
  listener: RejectedSessionListener,
): (() => void) => {
  listeners.add(listener);
  return () => listeners.delete(listener);
};

export const resetRejectedSessionDedupe = (): void => {
  lastReport = null;
};

/**
 * Temporarily suppress global reauthentication while intentionally ending an
 * account session. The returned release function is idempotent so callers can
 * safely use it in both catch and finally paths.
 */
export const suppressRejectedSessionReports = (): ((discardPending?: boolean) => void) => {
  reportSuppressionDepth += 1;
  let released = false;
  return (discardPending = false) => {
    if (released) return;
    released = true;
    reportSuppressionDepth = Math.max(0, reportSuppressionDepth - 1);
    if (reportSuppressionDepth > 0) return;

    const pendingReport = pendingSuppressedReport;
    pendingSuppressedReport = null;
    if (pendingReport && !discardPending) {
      reportRejectedSession(pendingReport);
    }
  };
};
